import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

export type StatusVariant = 'neutral' | 'info' | 'warning' | 'danger' | 'success'
export type StatusSize = 'sm' | 'md'

export interface StatusChipProps {
  children: ReactNode
  variant?: StatusVariant
  size?: StatusSize
  /** Kropka przed etykietą (jak nagłówki grup na ekranie terminów). */
  dot?: boolean
  className?: string
  title?: string
}

const VARIANTS: Record<StatusVariant, string> = {
  neutral: 'bg-chip-neutral-bg text-chip-neutral-fg', // „Doradztwo", „Zawieszona"
  info: 'bg-chip-info-bg text-chip-info-fg', // „W toku"
  warning: 'bg-chip-warning-bg text-chip-warning-fg', // „Do weryfikacji", „Oczekuje na termin"
  danger: 'bg-chip-danger-bg text-chip-danger-fg', // „Niska pewność"
  success: 'bg-chip-success-bg text-chip-success-fg', // „Zakończona", „Zweryfikowany"
}

const DOTS: Record<StatusVariant, string> = {
  neutral: 'bg-chip-neutral-fg',
  info: 'bg-chip-info-fg',
  warning: 'bg-warning',
  danger: 'bg-danger',
  success: 'bg-success',
}

const SIZES: Record<StatusSize, string> = {
  sm: 'h-[20px] px-1.5 text-[11.5px]',
  md: 'h-[23px] px-2 text-[12.5px]',
}

/** Chip statusu — „W toku", „Do weryfikacji", „Niska pewność" itd. */
export function StatusChip({ children, variant = 'neutral', size = 'md', dot = false, className, title }: StatusChipProps) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded font-medium leading-none',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
    >
      {dot ? <i className={cn('h-[6px] w-[6px] rounded-full', DOTS[variant])} /> : null}
      {children}
    </span>
  )
}

/** Etykieta kategorii (jasne tagi typu dokumentu: „Postanowienie", „Wezwanie"). */
export function Tag({ children, className, active = false }: { children: ReactNode; className?: string; active?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex h-[22px] items-center whitespace-nowrap rounded px-2 text-[12.5px] font-medium leading-none',
        active ? 'bg-navy-900 text-white' : 'bg-chip-info-bg text-chip-info-fg',
        className,
      )}
    >
      {children}
    </span>
  )
}
